import React, { FunctionComponent, useMemo } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { makeStyles } from '@rbx/ui';
import { resolveLanguage } from '../utils/codeBlockUtils';
import useSyntaxHighlightingStyles from '../hooks/useSyntaxHighlightingStyles';
import Renderer from '../Renderer';

export interface CodeBlockCoreStyleProps {
  maxContentHeight?: number;
  fullHeight?: boolean;
}

const useCodeBlockCoreStyles = makeStyles<CodeBlockCoreStyleProps>()((theme, props) => ({
  contentShrunk: {
    padding: 0,
    ...(props.fullHeight ? {} : { maxHeight: props.maxContentHeight }),
    overflow: 'auto',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  contentExpanded: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
}));

export interface CodeBlockCoreProps extends CodeBlockCoreStyleProps {
  content: string;
  language?: string;
  isExpanded?: boolean;
  className?: string;
}

/**
 * Core syntax highlighting component without any wrapper UI.
 * Use this when you want to embed syntax highlighting in custom layouts.
 */
const CodeBlockCore: FunctionComponent<CodeBlockCoreProps> = ({
  content,
  language,
  isExpanded = false,
  maxContentHeight,
  fullHeight = false,
  className,
}) => {
  const syntaxStyles = useSyntaxHighlightingStyles();
  const { classes } = useCodeBlockCoreStyles({ maxContentHeight, fullHeight });

  const resolvedLanguage = useMemo(() => resolveLanguage(language), [language]);

  return (
    <SyntaxHighlighter
      language={resolvedLanguage}
      style={syntaxStyles}
      wrapLines
      className={className}
      renderer={Renderer(isExpanded ? classes.contentExpanded : classes.contentShrunk)}
      customStyle={{
        padding: 0,
        margin: 0,
        tabSize: 4,
      }}
      lineProps={() => ({
        style: {
          paddingRight: '20px',
          paddingLeft: '20px',
          flex: 1,
        },
      })}>
      {content.trimEnd()}
    </SyntaxHighlighter>
  );
};

export default CodeBlockCore;
